import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, Minus, Trash2, Image, Palette, PawPrint, Shield, MoreVertical } from "lucide-react";
import { Button } from "./ui/button";
import { ScryfallCard } from "@/types/scryfall";
import { NostrEvent } from "@nostrify/nostrify";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface CardMenuProps {
  card: ScryfallCard & { count: number };
  listEvent: NostrEvent | null;
  onUpdate: () => void;
  onChangeArt: (card: ScryfallCard) => void;
  onSetCommander: (card: ScryfallCard) => void;
  onSetCompanion: (card: ScryfallCard) => void;
  onSetBanner: (card: ScryfallCard) => void;
  onUnsetCommander: (card: ScryfallCard) => void;
  onUnsetCompanion: (card: ScryfallCard) => void;
  isCommander: boolean;
  isCompanion: boolean;
  isSideboard?: boolean;
  className?: string;
}

export function CardMenu({
  card,
  listEvent,
  onUpdate,
  onChangeArt,
  onSetCommander,
  onSetCompanion,
  onSetBanner,
  onUnsetCommander,
  onUnsetCompanion,
  isCommander,
  isCompanion,
  isSideboard,
  className
}: CardMenuProps) {
  const { mutate: publish } = useNostrPublish();
  const { user } = useCurrentUser();

  if (!listEvent || user?.pubkey !== listEvent.pubkey) {
    return null;
  }

  const updateList = (newTags: string[][]) => {
    if (!listEvent) return;

    const newEvent = {
      kind: listEvent.kind,
      content: listEvent.content,
      tags: newTags,
      created_at: Math.floor(Date.now() / 1000),
    };

    publish(newEvent, {
      onSuccess: () => {
        onUpdate();
      }
    });
  };

  const handleAddCopy = () => {
    if (!listEvent) return;
    const tagType = isSideboard ? 'b' : 'c';
    const allTags = [...listEvent.tags];
    const relevantTags = allTags.filter(t => t[0] === tagType);
    const otherTags = allTags.filter(t => t[0] !== tagType);

    const cardTagIndex = relevantTags.findIndex(t =>
      t[1] === card.name &&
      t[3] === card.set &&
      t[4] === card.collector_number &&
      (t[6] === 'foil') === !!card.is_foil
    );

    if (cardTagIndex > -1) {
      const existingTag = relevantTags[cardTagIndex];
      const currentQuantity = parseInt(existingTag[2], 10);
      existingTag[2] = (currentQuantity + 1).toString();
    } else {
      relevantTags.push([
        tagType,
        card.name,
        "1",
        card.set,
        card.collector_number,
        card.lang || 'en',
        card.is_foil ? "foil" : ""
      ]);
    }

    updateList([...otherTags, ...relevantTags]);
  };

  const handleRemoveCopy = () => {
    if (!listEvent) return;
    const tagType = isSideboard ? 'b' : 'c';
    const allTags = [...listEvent.tags];
    const relevantTags = allTags.filter(t => t[0] === tagType);
    const otherTags = allTags.filter(t => t[0] !== tagType);

    const cardTagIndex = relevantTags.findIndex(t =>
      t[1] === card.name &&
      t[3] === card.set &&
      t[4] === card.collector_number &&
      (t[6] === 'foil') === !!card.is_foil
    );

    if (cardTagIndex > -1) {
      const existingTag = relevantTags[cardTagIndex];
      const currentQuantity = parseInt(existingTag[2], 10);

      if (currentQuantity > 1) {
        existingTag[2] = (currentQuantity - 1).toString();
      } else {
        relevantTags.splice(cardTagIndex, 1);
      }
    }

    updateList([...otherTags, ...relevantTags]);
  };

  const handleDeleteAll = () => {
    if (!listEvent) return;
    const tagType = isSideboard ? 'b' : 'c';
    const tagsToKeep = listEvent.tags.filter(t =>
      t[0] !== tagType ||
      (t[1] !== card.name ||
       t[3] !== card.set ||
       t[4] !== card.collector_number ||
       (t[5] === 'foil') !== (card.is_foil === 'foil'))
    );
    updateList(tagsToKeep);
  };

  const handleMove = () => {
    if (!listEvent) return;
    const fromTag = isSideboard ? 'b' : 'c';
    const toTag = isSideboard ? 'c' : 'b';

    const allTags = [...listEvent.tags];
    const fromTags = allTags.filter(t => t[0] === fromTag);
    const toTags = allTags.filter(t => t[0] === toTag);
    const otherTags = allTags.filter(t => t[0] !== fromTag && t[0] !== toTag);

    const cardTagIndex = fromTags.findIndex(t =>
      t[1] === card.name &&
      t[3] === card.set &&
      t[4] === card.collector_number &&
      (t[6] === 'foil') === !!card.is_foil
    );

    if (cardTagIndex > -1) {
      const [movedTag] = fromTags.splice(cardTagIndex, 1);
      movedTag[0] = toTag;
      toTags.push(movedTag);
    }

    updateList([...otherTags, ...fromTags, ...toTags]);
  };

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="w-5 h-9 sm:p-5 bg-background/80">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {!isSideboard && (
            <>
              {isCommander ? (
                <DropdownMenuItem onClick={() => onUnsetCommander(card)}>
                  <Shield className="mr-2 h-4 w-4" />
                  <span>Remove as Commander</span>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onSetCommander(card)}>
                  <Shield className="mr-2 h-4 w-4" />
                  <span>Add as Commander</span>
                </DropdownMenuItem>
              )}
              {isCompanion ? (
                <DropdownMenuItem onClick={() => onUnsetCompanion(card)}>
                  <PawPrint className="mr-2 h-4 w-4" />
                  <span>Unset as Companion</span>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onSetCompanion(card)}>
                  <PawPrint className="mr-2 h-4 w-4" />
                  <span>Set as Companion</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onSetBanner(card)}>
                <Image className="mr-2 h-4 w-4" />
                <span>Set as Banner</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={handleMove}>
            {isSideboard ? 'Move to Mainboard' : 'Move to Sideboard'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onChangeArt(card)}>
            <Palette className="mr-2 h-4 w-4" />
            <span>Change Art/Foil</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleAddCopy}>
            <Plus className="mr-2 h-4 w-4" />
            <span>Add Copy</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleRemoveCopy} disabled={card.count < 1}>
            <Minus className="mr-2 h-4 w-4" />
            <span>Remove Copy</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDeleteAll} className="text-red-500 focus:text-red-500">
            <Trash2 className="mr-2 h-4 w-4" />
            <span>Delete All</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
